
import { Vue, Options } from "vue-class-component";
import { ellipsisString, formatByCountry } from "@/app/infrastructures/misc/Utils";
import checkRolePermission from "@/app/infrastructures/misc/common-library/CheckRolePermission";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import { AccountController } from "@/app/ui/controllers/AccountController";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import router from "../../router";
import { CargoController } from "../../controllers/CargoController";
import { NGenController } from "../../controllers/NGenController";
import { CARGO } from "@/app/infrastructures/misc/RolePermission";
import { CargoData } from "@/domain/entities/Cargo";
import { capitalize } from "@vue/shared";
import Print from "./modules/print.vue";
import moment from "moment";
import { FlagsPermissionCargo } from "@/feature-flags/flags-permission-cargo";
import CargoFilterSection from "./component/cargo-filter-section.vue";

@Options({
  components: { Print, CargoFilterSection },
  computed: {
    ableToViewDetail() {
      return FlagsPermissionCargo.permission_cargo_detail_view.isEnabled();
    }
  }
})
export default class TabBookingInProgress extends Vue {
  created() {
    CargoController.setCargoStatus(
      "process-immediate,process,process-immediate-tracking,process-webhook,sabre"
    );
    CargoController.setStartDate(
      new Date(new Date(new Date().setDate(new Date().getDate() - 1))) as Date | null
    );
    CargoController.setEndDate(new Date() as Date | null);
  }

  mounted() {
    this.refs = this.$refs;
    CargoController.setFirstPage();
    this.fetchLastIdRetryCargo();
    this.startInterval();
    this.startIntervalDateTime();
    this.$posthog.startSessionRecording();
    this.fetchCargoSummary()
  }
  unmounted() {
    CargoController.onReset();
    this.stopInterval();
  }

  fetchInterval = null as any;
  dateTimeInterval = null as any;
  startIntervalDateTime() {
    this.dateTimeInterval = setInterval(() => {
      this.dateTimeNow = moment();
    }, 1000);
  }
  startInterval() {
    this.fetchInterval = setInterval(() => {
      this.fetchLastIdRetryCargo();
    }, FlagsPermissionCargo.config_interval_auto_refresh_cargo.getValue() * 1000);
  }
  stopInterval() {
    clearInterval(this.fetchInterval);
    clearInterval(this.dateTimeInterval);
  }

  lastIdRetryCargo = 0;
  async fetchRetryCargoListWithoutLoading() {
    await CargoController.fetchCargoList({ type: "retry" });
  }
  async fetchCargoList(type: string) {
    this.isLoading = true;
    await CargoController.fetchCargoList({ type });
    this.isLoading = false;
  }
  async fetchLastIdRetryCargo() {
    const newLastIdRetryCargo = await CargoController.fetchLastIdCargoRetryCargo(
      "retry-cargo"
    );
    if (this.lastIdRetryCargo === 0 && newLastIdRetryCargo !== 0) {
      this.lastIdRetryCargo = newLastIdRetryCargo;
    }
    if (this.lastIdRetryCargo === 0 || newLastIdRetryCargo === 0) return;
    if (this.lastIdRetryCargo !== newLastIdRetryCargo) {
      this.lastIdRetryCargo = newLastIdRetryCargo;
      await this.fetchRetryCargoListWithoutLoading();
    }
  }

  cancelCargo() {
    CargoController.cancelCargo();
    this.handlerModalCancelBooking(false);
  }

  // loading
  isLoading = false;

  // error cause
  get errorCause() {
    return CargoController.errorCause;
  }

  showModalCancelBooking = false;
  handlerModalCancelBooking(val: boolean) {
    this.showModalCancelBooking = val;
  }
  get countryCode() {
    return AccountController.accountData?.countryCode ?? "ID";
  }

  formatValueNumber(number: number) {
    return formatByCountry(number, { type: "weight", country: this.countryCode });
  }

  get columns() {
    return [
      {
        name: "No. Booking",
        styleHead: "w-56 text-left whitespace-no-wrap align-top break-all",
        render: (item: CargoData) => {
          return `<p class="w-full break-all text-left">${
            this.convertCargoNo(item.bookingRequestId, item.cargoNumber)
              .converted
          }</p>`;
        }
      },
      {
        name: "ETD/ETA",
        styleHead: "w-56 text-left",
        render: (item: CargoData) => {
          return `
<div class="flex flex-col gap-1.5 text-sm text-gray-lp-300" >
  
  ${this.formatDateCargo(item.departureDate, "DD MMMM YYYY")}
  <div class="flex flex-row gap-1 items-center">
  <div class="text-gray-lp-300">
  ${this.handleEstimationArrivalDateTime(item)}
  </div>
</div>
`
        }
      },
      {
        name: "Kota asal - tujuan",
        styleHead: "w-56 text-left whitespace-no-wrap align-middle break-all",
        render: () => {
          return ``;
        },
        "custom-slot": true,
        slotName: "origin_destination"
      },
      {
        name: this.$t("No. Penerbangan"),
        styleHead: "w-60 text-left whitespace-no-wrap align-middle",
        render: (item: CargoData) => {
          return `<div class="flex flex-row items-center gap-x-1 overflow-ellipsis text-left">
          ${ellipsisString(item.vehiclePoliceNumber, 14) || "-"}
          ${item.totalFlight > 0 ? `<div class="text-gray-lp-300 px-1 text-10px bg-gray-lp-200 font-medium rounded">${item.totalFlight}x transit</div>`: ''}
          </div>`;
        }
      },
      {
        name: this.$t("Tanggal dibuat"),
        styleHead: "w-56 text-left whitespace-no-wrap align-middle",
        render: (item: CargoData) => {
          return `<div class="flex flex-col text-sm text-gray-lp-300"><div class="overflow-ellipsis text-left">${this.formatDateCargo(
            item.createdAt, "DD MMMM YYYY, HH:mm"
          )} WIB<div class="truncate text-gray-lp-600 w-32">${
            item.createdBy
          }</div>
            <div>${item.cargoHubName}</div>
          </div>`;
        }
      },
      {
        name: "Total STT/koli",
        styleHead: "w-36 text-left whitespace-no-wrap align-top",
        render: (item: CargoData) => {
          return `<div class="overflow-ellipsis text-left">${item.totalStt}/${item.cargoActualPiece}</div>`;
        }
      },
      {
        name: this.$t("CW kargo"),
        styleHead: "w-36 whitespace-no-wrap align-top text-left",
        render: (item: CargoData) => {
          return `<div class="overflow-ellipsis text-left">${this.formatValueNumber(
            item.cargoTotalCustomChargeableWeight
          )} kg</div>`;
        }
      },
      {
        name: "Berat kotor/volume (kg)",
        styleHead: "w-56 text-left whitespace-no-wrap align-top",
        render: (item: CargoData) => {
          return `<div class="overflow-ellipsis text-left">${this.formatValueNumber(
            item.totalActualCargoGrossWeight
          )} kg/${item.totalActualCargoVolumeWeight}</div>`;
        }
      },
      {
        name: "Action",
        key: "actionable_column",
        styleHead: "w-28 text-left align-top",
        showButton: (item: CargoData) => {
          if (this.configTimeoutCancelCargo === 0) return true;
          const created = this.$moment(item.createdAt);
          const now = this.dateTimeNow;
          const diff = now.diff(created, "minutes");
          return diff >= this.configTimeoutCancelCargo;
        }
      }
    ];
  }

  dateTimeNow = moment();

  airportDestinationCode(item: CargoData) {
    return item.cargoType === "plane"
      ? `${item.airportDestinationCityCode} - ${item.airportDestinationCityName}`
      : "-";
  }

  get configTimeoutCancelCargo() {
    return FlagsPermissionCargo.config_time_cancel_cargo.getValue();
  }


  get isActionAble() {
    return FlagsPermissionCargo.feature_flight_v2_enable.isEnabled();
  }

  actionGroupOptions = (item: any) => {
    return [
      {
        icon: "x-circle-outline-gray",
        label: "Batalkan",
        clickFunction: () => {
          CargoController.setCargoIdToCancelOrDelete(item.id);
          CargoController.setCargoNoForModal(item.cargoNumber);
          this.handlerModalCancelBooking(true);
        }
      }
    ];
  };

  convertCargoNo(bookingRequestId: string, cargoNumber: string) {
    const result = bookingRequestId ? bookingRequestId : cargoNumber;
    return { converted: ellipsisString(result, 40), result };
  }

  cityColumnData(cityCode: string, cityName: string) {
    return cityCode && cityName ? `${cityCode} - ${cityName}` : "-";
  }

  cargoTypeColumnData(type: string) {
    return type ? capitalize(type) : "-";
  }

  async downloadEPL(id: number) {
    await CargoController.getDownloadData({ id: id, type: "epl" });
  }

  async downloadManifest(id: number) {
    await CargoController.getDownloadData({ id: id, type: "cargo" });
  }

  printAWBLionCargo(AWBNo: number) {
    NGenController.printAWBLionCargo({ AWBNo: AWBNo });
  }

  // Print
  check = null;
  refs: any = "";
  cargoNumber = "";

  chevronUp: boolean[] = [];

  get pagination() {
    return CargoController.retryCargo.pagination;
  }

  get cargoList() {
    return CargoController.retryCargo.data;
  }

  get cargoListAvailable() {
    return (
      !this.cargoList.length && !this.isLoading && this.pagination.page === 1
    );
  }

  // detail
  onClickRow(item: CargoData) {
    router.push(
      `/cargo/${encodeURIComponent(
        this.convertCargoNo(item.bookingRequestId, item.cargoNumber).result
      )}?rc_no=${item.id}`
    );
  }

  formatDateCargo(date: any, format = "DD MMMM YYYY", isUtcOffset = true): string{
    const offset = this.$moment(date).locale("id").utcOffset(date);
    const withoutOffset = this.$moment(date).locale("id");
    return isUtcOffset ? offset.format(format) : withoutOffset.format(format);
  }

  airportOriginCode(item: CargoData) {
    return item.cargoType === "plane"
      ? `${item.airportOriginCityCode} - ${item.airportOriginCityName}`
      : "-";
  }

  formatEstimationDepartureArrival(departureDate: string, arrivalDate: string) {
    return `${this.formatDateCargo(departureDate, "HH:mm", false)} - ${this.formatDateCargo(arrivalDate, "HH:mm", false)}`
  }

  getDiffDay(departureDate: any, arrivalDate: any, splitBy = "T") {
    if (!departureDate || !arrivalDate)return 0
    else {
      return this.$moment(arrivalDate?.split(splitBy)[0]).utcOffset(arrivalDate).diff(this.$moment(departureDate?.split(splitBy)[0]).utcOffset(departureDate), "days");
    }
  }

  handleEstimationArrivalDateTime(item: any) {
    const departure = item.estimationDepartureDate
    const arrival = item.estimationArrivalDate
    return `${
      !departure || !arrival
        ? "-"
        : `${this.formatEstimationDepartureArrival(departure, arrival)}
            </div>
            ${
              this.getDiffDay(departure, arrival, " ") > 0
                ? `<div class="text-gray-lp-300 px-1 text-10px bg-gray-lp-200 font-medium rounded">+${this.getDiffDay(
                    departure,
                    arrival,
                    " "
                  )} ${this.$t("hari")}</div>
            </div>`
                : ""
            }`
    }`;
  }

  fetchCargoSummary() {
    CargoController.getCargoSummaryInProcess()
  }

  onHandleFilter() {
    this.fetchCargoList("retry");
    this.fetchCargoSummary()
  }
}
